import React, { useEffect, useState } from 'react';
import { listerFacturesDuSite, urlDocument } from '../../services/api';
import OrderDetailsModal from './OrderDetailsModal';

const formatDate = (iso) => (iso ? iso.split('-').reverse().join('/') : '—');
const euros = (n) =>
  new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

/**
 * Toutes les factures émises par le site (série JR-AAAA-NNNN, depuis le
 * 23/09/2026), avec recherche. Le tableau de bord ne montre que les 50
 * dernières commandes : c'est ici qu'on imprime ou qu'on crédite une facture
 * plus ancienne. La fiche commande s'ouvre d'un clic (note de crédit).
 */
export default function FacturesDuSite() {
  const [saisie, setSaisie] = useState('');
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(1);
  const [selection, setSelection] = useState(null);
  // Relecture après la fermeture d'une fiche : une note de crédit a pu y être émise.
  const [tour, setTour] = useState(0);
  // La réponse porte la clé de la requête qui l'a produite : tant qu'elle ne
  // correspond pas à la requête courante, on est en train de charger.
  const cle = `${recherche}|${page}|${tour}`;
  const [etat, setEtat] = useState({ cle: null, erreur: null, donnees: null });

  useEffect(() => {
    let annule = false;
    listerFacturesDuSite(recherche, page)
      .then((donnees) => !annule && setEtat({ cle, erreur: null, donnees }))
      .catch((e) =>
        !annule &&
        setEtat({
          cle,
          erreur: e.response?.data?.error || 'Les factures du site n’ont pas pu être chargées.',
          donnees: null,
        })
      );
    return () => {
      annule = true;
    };
  }, [cle, recherche, page]);

  const chargement = etat.cle !== cle;

  const chercher = (e) => {
    e.preventDefault();
    setPage(1);
    setRecherche(saisie.trim());
  };

  const factures = etat.donnees?.factures || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Factures émises par le site</h2>
          <p className="text-sm text-gray-500">
            Série JR-AAAA-NNNN (Sébastien Schoonjans, depuis le 23/09/2026) — une par vente payée.
            {etat.donnees ? ` ${etat.donnees.total} facture(s).` : ''}
          </p>
        </div>
        <form onSubmit={chercher} className="flex gap-2">
          <input
            type="search"
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            placeholder="N° de facture, de commande ou e-mail"
            aria-label="Rechercher une facture"
            className="w-64 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
          <button
            type="submit"
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Rechercher
          </button>
        </form>
      </div>

      {etat.erreur && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{etat.erreur}</div>
      )}

      {chargement ? (
        <div className="rounded-xl bg-white p-8 text-center text-gray-500 shadow-sm">Chargement…</div>
      ) : factures.length === 0 ? (
        <div className="rounded-xl bg-white p-8 text-center text-gray-500 shadow-sm">
          {recherche ? 'Aucune facture ne correspond à cette recherche.' : 'Aucune facture émise par le site pour l’instant.'}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Facture', 'Opération', 'Commande', 'Client', 'Total TVAC', 'Note de crédit', ''].map((t) => (
                    <th
                      key={t}
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      {t}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {factures.map((f) => (
                  <tr key={f.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 font-medium text-gray-900">
                      {f.factureNumero}
                      {f.factureAlertes?.length > 0 && (
                        <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800" title={f.factureAlertes.join(', ')}>
                          à vérifier
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-600">{formatDate(f.facture?.dateOperation)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-600">{f.orderNumber}</td>
                    <td className="px-4 py-3 text-gray-600">{f.facture?.client || f.user?.email || '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-900">{euros(f.facture?.totalTtc ?? f.totalPrice)}</td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {f.noteDeCreditNumero ? (
                        <a
                          href={urlDocument(f.noteDeCreditChemin)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-red-700 hover:underline"
                        >
                          {f.noteDeCreditNumero}
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <a
                        href={urlDocument(f.factureChemin)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mr-3 font-medium text-blue-600 hover:underline"
                      >
                        Imprimer
                      </a>
                      <button
                        type="button"
                        onClick={() => setSelection(f)}
                        className="font-medium text-gray-700 hover:underline"
                      >
                        Fiche commande
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {etat.donnees?.pages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded px-3 py-1 text-gray-700 hover:bg-gray-100 disabled:opacity-40"
              >
                ← Plus récentes
              </button>
              <span className="text-gray-500">
                Page {etat.donnees.page} / {etat.donnees.pages}
              </span>
              <button
                type="button"
                disabled={page >= etat.donnees.pages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded px-3 py-1 text-gray-700 hover:bg-gray-100 disabled:opacity-40"
              >
                Plus anciennes →
              </button>
            </div>
          )}
        </div>
      )}

      {selection && (
        <OrderDetailsModal
          order={selection}
          onClose={() => {
            setSelection(null);
            setTour((t) => t + 1);
          }}
        />
      )}
    </div>
  );
}
